import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { PaymentActivityDetails, PaymentRequestedActivityDetails } from '@paintscout/api'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import classnames from 'classnames'
import striptags from 'striptags'
import ActivityTitle from '../ActivityTitle'
import ActivityFooter from '../ActivityFooter'
import ActivityContent from '../ActivityContent'
import Highlight from '../../Highlight'
import type { ActivityTypeComponentProps } from '../Activity'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'

import ActivityOwner from '../ActivityOwner'
import ActivityDot from '../ActivityDot'
import Typography from '../../Typography'
import HtmlContent from '../../HtmlContent'
import Collapse from '../../Collapse'
import IconButton from '../../IconButton'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps>((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 'fit-content',
    '& > div': {
      width: '100%'
    }
  },
  email: {
    maxHeight: 150,
    overflowY: 'auto'
  },
  emailTo: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.paintscout.palette.brand.Neutral.P60
  },
  emailSubject: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  emailContent: {
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginTop: theme.spacing(1)
  },
  collapseText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  }
}))

export default function QuotePaymentRequestedActivity(props: ActivityTypeComponentProps) {
  const { activity, showDocTypeDetails, showContent } = props
  const classes = useStyles(props)
  const { note } = activity.details as PaymentActivityDetails
  const { status, email } = activity.details as PaymentRequestedActivityDetails
  const [collapsed, setCollapsed] = useState(true)

  const to = email?.to ?? []
  const subject = email?.subject ?? ''
  const content = email?.content ?? ''

  const handleCollapseChange = (e, isOpen: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    setCollapsed(isOpen)
  }

  return (
    <>
      <ActivityTitle>
        <ActivityDot activity={activity} />
        <>
          {showDocTypeDetails ? (
            <>
              <Highlight>Payment Request </Highlight>
              was <Highlight>{status === 'resent' ? 'Resent' : 'Sent'}</Highlight>
              <ActivityOwner activity={activity} />
            </>
          ) : (
            <>
              <Highlight>Payment </Highlight>
              <Highlight>Requested</Highlight>
              <ActivityOwner activity={activity} />
            </>
          )}
        </>
      </ActivityTitle>
      {showContent && (
        <ActivityContent className={classes.content}>
          {email ? (
            <>
              <Collapse show={collapsed}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.collapseText}
                  style={{ height: 16, width: 'fit-content' }}
                  onClick={(e) => handleCollapseChange(e, false)}
                >
                  View message
                  <IconButton size="small" style={{ marginLeft: 8 }}>
                    <KeyboardArrowDownIcon />
                  </IconButton>
                </Typography>
              </Collapse>
              <Collapse show={!collapsed}>
                <div className={classes.email}>
                  <Typography
                    variant="body1"
                    component="h5"
                    className={classnames(classes.emailSubject, classes.collapseText)}
                    onClick={(e) => handleCollapseChange(e, true)}
                  >
                    {striptags(subject)}
                    <IconButton size="small">
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </Typography>
                  <Typography variant="subtitle2" component="h4" className={classes.emailTo}>
                    to {to.join(', ')}
                  </Typography>
                  <HtmlContent content={content} classes={{ root: classes.emailContent }} />
                </div>
              </Collapse>
            </>
          ) : (
            !!note && <>{note}</>
          )}
        </ActivityContent>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter timestamp={activity.details.timestamp} />
    </>
  )
}
